import React from 'react';
import styled from 'styled-components';
import { Query } from 'react-apollo';
import { themes, graphql, PatientLabQueryResponse, utils } from 'kb-shared';

import Modal from '../../Modal';
import { ReactComponent as CloseIcon } from 'assets/icons/close-icon.svg';
import { ReactComponent as MessagingIcon } from 'assets/icons/messaging.svg';
import WhatsappDetails from './WhatsappDetails';
import KlaraDetails from './KlaraDetails';

const { breakpoints } = themes;
const { KBContacts, labs } = utils;

interface Props {
  visible: boolean;
  onDismiss: Function;
}
interface Lab {
  labelText: string;
  number: string;
  usesKlara?: boolean;
}

const LABS: Record<string, Lab> = Object.freeze({
  '0': {
    labelText: KBContacts.nyWhatsappNumber.replace('-', ' ').slice(4),
    number: KBContacts.nyWhatsappNumber
  },
  [labs(process.env.REACT_APP_ENV).NY]: {
    labelText: KBContacts.nyWhatsappNumber.replace('-', ' ').slice(4),
    number: KBContacts.nyWhatsappNumber
  },
  [labs(process.env.REACT_APP_ENV).SF]: {
    labelText: KBContacts.sfWhatsappNumber.replace('-', ' ').slice(4),
    number: KBContacts.sfWhatsappNumber,
    usesKlara: true
  },
  [labs(process.env.REACT_APP_ENV).LA]: {
    labelText: KBContacts.laWhatsappNumber.replace('-', ' ').slice(4),
    number: KBContacts.laWhatsappNumber,
    usesKlara: true
  },
  [labs(process.env.REACT_APP_ENV).LOS_ALTOS]: {
    labelText: KBContacts.losAltosWhatsappNumber.replace('-', ' ').slice(4),
    number: KBContacts.losAltosWhatsappNumber,
    usesKlara: true
  },
  [labs(process.env.REACT_APP_ENV).ATLANTA]: {
    labelText: KBContacts.atlantaWhatsappNumber.replace('-', ' ').slice(4),
    number: KBContacts.atlantaWhatsappNumber
  },
  [labs(process.env.REACT_APP_ENV).AUSTIN]: {
    labelText: KBContacts.austinWhatsappNumber.replace('-', ' ').slice(4),
    number: KBContacts.austinWhatsappNumber
  },
  [labs(process.env.REACT_APP_ENV).MINNEAPOLIS]: {
    labelText: KBContacts.minneapolisWhatsappNumber.replace('-', ' ').slice(4),
    number: KBContacts.minneapolisWhatsappNumber
  }
});

const IHaveMoreQuestionsModal = ({ visible, onDismiss }: Props) => (
  <Query query={graphql.query.PATIENT_LAB}>
    {({ data, error, loading }: PatientLabQueryResponse) => {
      const labId = !data || !data.patientLab || loading || error ? '0' : data.patientLab.lab.id;
      const lab = LABS[labId] || LABS['0']; // hotfix for princeton & virtual markets;

      return (
        <Modal open={visible} onRequestClose={() => onDismiss()}>
          <Container>
            <Content>
              <IconContainer onClick={() => onDismiss()}>
                <CloseIcon />
              </IconContainer>
              <Title>
                <MessagingIcon /> Contact your Nurse team
              </Title>
              {lab.usesKlara ? <KlaraDetails /> : <WhatsappDetails lab={lab} />}
            </Content>
          </Container>
        </Modal>
      );
    }}
  </Query>
);

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Content = styled.div`
  padding: 45px 45px 30px 16px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background-color: #ffffff;
  border-left: 8px solid #ffe664;

  @media (min-width: ${breakpoints.tabletLandscape}) {
    padding: 45px 45px 20px 24px;
    min-width: 500px;
  }
`;

const IconContainer = styled.div`
  cursor: pointer;
  position: absolute;
  right: 12px;
  top: 12px;
`;

export const Title = styled.h1`
  ${themes.fonts.DomaineDisplay}
  font-size: 28px;
  color: ${themes.colors.night};
  margin-bottom: 24px;
  display: grid;
  grid-template-columns: 35px auto;
  grid-gap: 16px;
  align-items: center;

  & > svg {
    transform: scale(1.5);
    justify-self: flex-end;
  }
`;

export default IHaveMoreQuestionsModal;
